import { AccountCircle, ListAlt, Login, PeopleAlt } from "@mui/icons-material";
import { SelectChangeEvent, useMediaQuery } from "@mui/material";
import React, { memo, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";

const HeaderPanel = React.lazy(() => import("MyAccountPlus-UIComponents/src/components/header/HeaderPanel"));

import { useAppDispatch, useAppSelector } from "../store/hooks";
import { loginActions } from "../store/login-reducer/loginSlice";
import { profileActions } from "../store/admin-reducer/profile-reducer/profileSlice";
import { accountActions } from "../store/admin-reducer/account-reducer/accountsSlice";
import { getPermissionsThunk, listTypesActions } from "../store/list-types-reducer/listTypesSlice";
import { getUserAccountsThunk, userAccountActions } from "../store/user-reducers/user-account-reducer/userAccountSlice";

interface Props {
  window?: () => Window;
}

const loginItems = [
  { name: "Log In", link: "/login", icon: <Login />, subMenu: [] }
];

const Header = ({ window }: Props) => {

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const isTabletView = useMediaQuery("(max-width: 960px)");

  const userInfo = sessionStorage.userInfo ? JSON.parse(sessionStorage.userInfo) : undefined;
  const adminInfo = sessionStorage.adminInfo ? JSON.parse(sessionStorage.adminInfo) : undefined;

  const { permissions } = useAppSelector(state => state.listStore);
  const { user } = useAppSelector((state) => state.adminProfileStore);
  const { setting } = useAppSelector((state) => state.appSettingStore);
  const { account, overviewAccounts, filteredTabs } = useAppSelector((state) => state.userAccountStore);

  const navUserItems = useMemo(() => {
    return [
      {
        name: "Accounts",
        link: "/accounts",
        icon: <ListAlt />,
        subMenu: [],
        onClick: () => {
          // If we're already on an account page, just navigate to the base accounts page
          if (pathname.includes('/accounts/')) {
            navigate('/accounts');
            return;
          }

          if (overviewAccounts.length > 0) {
            const accountId = overviewAccounts[0].account_id;
            dispatch(userAccountActions.accountsReducer(accountId));
            navigate(`/accounts/${accountId}`);
          } else {
            navigate("/accounts");
          }
        }
      },
      ...(setting.show_contact_us
        ? [{ name: "Contact Us", link: "/contact", icon: <ListAlt />, subMenu: [] }]
        : []),
      { name: "Profile", link: "/profile", icon: <AccountCircle />, subMenu: [] },
      // { name: "Apply For Credit", link: "/residential-credit", icon: <CreditCard /> },
    ];
  }, [setting.show_contact_us, overviewAccounts, dispatch, navigate, account, pathname]);

  const navAdminItems = [
    { name: "Profile", link: "/admin/profile", icon: <AccountCircle />, subMenu: [] },
    // { name: "Users", link: "/admin/users", icon: <PeopleAlt />, subMenu: [] },
    {
      name: "Account Portal",
      link: "/admin/accounts",
      icon: <ListAlt />,
      subMenu: [],
    },
  ];

  const handleLogout = () => {
    if (adminInfo) {
      // When returning from "Login As", restore admin session
      dispatch(profileActions.clearState());
      dispatch(userAccountActions.clearState());
      dispatch(listTypesActions.cleaState());
      dispatch(userAccountActions.orderSuccessReducer());
      sessionStorage.setItem("userInfo", sessionStorage.adminInfo);
      sessionStorage.removeItem("adminInfo");
      navigate(localStorage.getItem("last_route") || "/admin/accounts");
      localStorage.removeItem("last_route");
    } else {
      // Normal logout flow
      dispatch(profileActions.clearState());
      dispatch(loginActions.logoutReducer());
      dispatch(listTypesActions.cleaState());
      dispatch(userAccountActions.clearState());
      dispatch(accountActions.clearUserState());
      dispatch(userAccountActions.orderSuccessReducer());
      navigate("/login");
    }
  };

  useEffect(() => {
    // when user referesh page at profile then click on Account's overview at that time need overview
    if (isTabletView && overviewAccounts.length === 0 && userInfo?.role === "User") {
      dispatch(getUserAccountsThunk());
    }
    if (sessionStorage.userInfo) {
      dispatch(getPermissionsThunk())
    }
  }, [sessionStorage.adminInfo, sessionStorage.userInfo, dispatch]);

  const accountChangeHandler = (event: SelectChangeEvent) => {
    const accountId = event.target.value;
    dispatch(userAccountActions.accountsReducer(accountId));
    navigate(`/accounts/${accountId}`);
  };

  const userAccountActionsHandler = () => {
    const accountId = pathname.split("/")[2];
    if (accountId) {
      dispatch(userAccountActions.accountsReducer(accountId));
    }
  };

  return (
    <Box className="header-wrapper">
      <HeaderPanel
        userInfo={userInfo}
        account={account}
        overviewAccounts={overviewAccounts}
        user={user}
        setting={setting}
        handleLogout={handleLogout}
        adminInfo={adminInfo}
        accountChangeHandler={accountChangeHandler}
        userAccountActionsHandler={userAccountActionsHandler}
        fif={false}
        navAdminItems={navAdminItems}
        navUserItems={navUserItems}
        loginItems={pathname.includes("registration-confirm") ? [] : loginItems}
        pathname={pathname}
      />
    </Box>
  );
};
export default memo(Header);
